import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import ToggleButton from '@mui/material/ToggleButton';
import Tooltip from '@mui/material/Tooltip';

/**
 * The six anchors, in reading order — the array index is the cell's place in the 3x2 grid.
 *
 * The video's centre is deliberately absent: a banner there covers the face of whoever is
 * speaking, which is the opposite of what a testimonial wants.
 */
export const POSITION_CELLS = [
  { value: 'top_left', label: 'Top left' },
  { value: 'top_center', label: 'Top centre' },
  { value: 'top_right', label: 'Top right' },
  { value: 'bottom_left', label: 'Bottom left' },
  { value: 'bottom_center', label: 'Bottom centre' },
  { value: 'bottom_right', label: 'Bottom right' }
];

/**
 * Position picker shared by the title and the watermark.
 *
 * Both render the full 3x2 grid so the two controls read as the same piece. The watermark
 * passes only its four corners in `enabled`, leaving the centre cells visible but inert —
 * the burner has no overlay position for them.
 *
 * The cells carry `data-position` because their visible label is a tooltip, not text: the
 * attribute is what lets a test assert the grid's order without depending on hover.
 */
const VpPositionGrid = ({ value, enabled, onChange, label }) => (
  <Box
    role="radiogroup"
    aria-label={label}
    sx={{
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: 0.5,
      // An explicit width, not maxWidth: the cells are `1fr` and carry `minWidth: 0`, so they
      // have no intrinsic width to size the grid from. With only a maxWidth the whole thing
      // collapses to slivers wherever it is not stretched by its parent.
      width: 174,
      flexShrink: 0
    }}
  >
    {POSITION_CELLS.map((cell) => {
      const isEnabled = enabled.includes(cell.value);
      const isSelected = value === cell.value;

      return (
        // Tooltip needs a non-disabled wrapper to receive the hover events a disabled
        // button never fires.
        <Tooltip key={cell.value} title={cell.label}>
          <span>
            <ToggleButton
              value={cell.value}
              size="small"
              role="radio"
              aria-label={cell.label}
              aria-checked={isSelected}
              data-position={cell.value}
              disabled={!isEnabled}
              selected={isSelected}
              onClick={() => onChange(cell.value)}
              sx={{ width: '100%', height: 38, p: 0, minWidth: 0 }}
            />
          </span>
        </Tooltip>
      );
    })}
  </Box>
);

VpPositionGrid.propTypes = {
  /** One of POSITION_CELLS' values. */
  value: PropTypes.string,
  /** Which positions are selectable; the rest render disabled. */
  enabled: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  /** Names the group for screen readers — the two pickers must not sound alike. */
  label: PropTypes.string
};

VpPositionGrid.defaultProps = {
  value: null,
  label: 'Position'
};

export default VpPositionGrid;



// WEBPACK FOOTER //
// ./src/vp-ui/components/VpPositionGrid/VpPositionGrid.jsx